import React from 'react'

import { useQuery } from '@tanstack/react-query'

import getDistance from 'geolib/es/getDistance'

import { DateTime } from 'luxon'

import { ParkingContext } from '../context/ParkingContext'

import { useGeoLocation } from './useGeoLocation'

import { useQueryParams } from './useQueryParams'

import { axios } from '../services/axios'

import { Parking } from '../@types/entities/entities.types'

import {
  ParkingCharacteristic,
  UseParkingReturn,
} from '../helpers/types/hooks/parking.types'

export function useParking(): UseParkingReturn {
  const context = React.useContext(ParkingContext)

  const { location } = useGeoLocation()

  const query = useQueryParams()

  const { isRefetching, refetch } = useQuery(['parkings'], fetchParking, {
    enabled: location.lat !== -19.9262762 && location.lng !== -43.938171,
  })

  async function fetchParking() {
    const response = await axios.get<Parking[]>('/parking/filter/parkings', {
      params: {
        lat: location.lat,
        long: location.lng,
        range: 12,
        has_monthly: query.get('has_monthly'),
      },
    })

    const parkings = response.data.map(parking => {
      const distanceInMeters = getDistance(
        {
          latitude: location.lat,
          longitude: location.lng,
        },
        {
          latitude: parking.parking_address.latitude,
          longitude: parking.parking_address.longitude,
        }
      )

      let distance: string = `${distanceInMeters} m`

      if (distanceInMeters > 1000) {
        const distanceInKilometers = (distanceInMeters / 1000).toFixed(2)

        distance = `${distanceInKilometers} km`
      }

      const parking_business_hours = parking.parking_business_hours.map(pbh => {
        const hour_start_formatted = DateTime.fromISO(pbh.hour_start, {
          zone: 'America/Sao_Paulo',
          locale: 'pt-BR',
        }).toFormat('HH:mm')

        const hour_end_formatted = DateTime.fromISO(pbh.hour_end, {
          zone: 'America/Sao_Paulo',
          locale: 'pt-BR',
        }).toFormat('HH:mm')

        return {
          ...pbh,
          hour_start_formatted,
          hour_end_formatted,
        }
      })

      const parking_characteristics_active =
        parking.parking_characteristics.filter(pc => !pc.disabled)

      const parking_characteristics_formatted =
        parking_characteristics_active.reduce(
          (acc, characteristic, currentIndex) => {
            const description =
              ParkingCharacteristic[
                characteristic.characteristic
                  .name as keyof typeof ParkingCharacteristic
              ]

            if (acc === '') {
              return description
            }

            const remainingCharacteristics =
              parking_characteristics_active.length - currentIndex

            if (currentIndex === 3) {
              return `${acc} + ${remainingCharacteristics}`
            }

            if (currentIndex > 3) return acc

            return `${acc} • ${description}`
          },
          ''
        )

      return {
        ...parking,
        picture:
          parking.picture !== null
            ? parking.picture
            : 'https://parkoff-prod.s3.us-east-2.amazonaws.com/assets/svg/logo-white-2.svg',
        parking_configurations: {
          ...parking.parking_configurations,
          hour_price_formatted: new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(Number(parking.parking_configurations.hour_price)),
        },
        parking_business_hours,
        parking_address: {
          ...parking.parking_address,
          distance,
        },
        parking_characteristics_formatted,
      }
    })

    context.setParkings(parkings)

    return parkings
  }

  return {
    parkings: context.parkings,
    setParkings: context.setParkings,
    refetch,
    isRefetching,
  }
}
